import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Bot, X, Send, Mail, MessageCircle, Check, ChevronDown, ChevronUp, ChevronLeft } from 'lucide-react';
import { useAuth } from '../contexts/FirebaseAuthContext';
import { collection, addDoc, serverTimestamp, getDocs, query, where, orderBy, updateDoc, doc, getDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useToast } from '../hooks/useToast';
import { isReferralSystemEnabled } from '../lib/referralSystem';
import { ysr2Mobile } from '../features/shared/styles/mobileDesignSystem';

/** Above MobileLiveDashboard / song-request overlays (z ~ 2147483000) so Melody is always on top */
const MELODY_Z_MAIN = 2147483645;
const MELODY_Z_OVERLAY = 2147483646;

const MelodyChatbot = ({ isOpen, onClose, intent = null, onIntentHandled }) => {
    const { user } = useAuth();
    const { toast } = useToast();
    const messagesEndRef = useRef(null);
    const [inputValue, setInputValue] = useState('');
    const [messages, setMessages] = useState([]);
    const [isTyping, setIsTyping] = useState(false);
    const [showContactForm, setShowContactForm] = useState(false);
    const [contactForm, setContactForm] = useState({ subject: '', message: '' });
    const [isSubmittingContact, setIsSubmittingContact] = useState(false);
    const [showSupportConversation, setShowSupportConversation] = useState(false);
    const [supportConversation, setSupportConversation] = useState(null);
    const [supportConversationHistory, setSupportConversationHistory] = useState([]);
    const [originalSubmission, setOriginalSubmission] = useState(null);
    const [supportResponse, setSupportResponse] = useState('');
    const [isSubmittingSupportResponse, setIsSubmittingSupportResponse] = useState(false);
    const [expandedCategories, setExpandedCategories] = useState({});
    const [referralSystemEnabled, setReferralSystemEnabled] = useState(true);
    /** Full-height Guides vs Chat (avoids cramped half-screen FAQ + chat) */
    const [melodyMainTab, setMelodyMainTab] = useState('faqs');
    /** Full-screen answer sheet for a single FAQ */
    const [faqAnswerSheet, setFaqAnswerSheet] = useState(null);

    // Category-based questions with dropdowns
    const questionCategories = [
        {
            id: 'going-live',
            title: 'Going Live',
            questions: [
                "How do I go live?",
                "What do I need before going live?",
                "How do I start a live session?",
                "How do I end a live session?",
                "Can I schedule a live session?",
                "What happens when I go live?",
                "How do fans find me when I'm live?",
                "How do I share my live session?"
            ]
        },
        {
            id: 'catalogue-setlists',
            title: 'Song Catalogue & Setlists',
            questions: [
                "How do I add songs to my catalogue?",
                "How do I search for songs?",
                "How do I set song prices?",
                "What's the minimum song price?",
                "How do I create a setlist?",
                "How do I edit or delete songs?",
                "Can I bulk upload songs?",
                "How do I organize my songs?",
                "What are song tags?",
                "How do I archive songs?"
            ]
        },
        {
            id: 'payouts-balances',
            title: 'Payouts & Balances',
            questions: [
                "How do I check my earnings?",
                "How do I request a payout?",
                "What's the minimum payout amount?",
                "How long do payouts take?",
                "How do I view my payout history?",
                "What payment methods are available?",
                "How are my earnings calculated?",
                "When do I get paid?",
                "How do I update my payment details?"
            ]
        },
        {
            id: 'more',
            title: 'More...',
            questions: [
                "How can I contact you?",
                "How do I use Request Rewards?",
                "How do I share my referral link?",
                "How do I manage song requests?",
                "How do I update my profile?",
                "How do I change my profile URL?",
                "How do I add a profile picture?",
                "How do I schedule gigs?",
                "How do I use the calendar?",
                "What are creative assets?",
                "How do I get help?"
            ]
        }
    ];

    const chatbotResponses = {
        // Going Live
        "go live": "To go live: 1) Open your Live Dashboard, 2) Click 'Start Live Session' or toggle the Live switch, 3) Set your session duration (optional), 4) Your public page will automatically show you're live, 5) Fans can request songs in real-time! Make sure you have at least 5 songs in your catalogue before going live.",
        "start live": "To start a live session: 1) Navigate to your Live Dashboard, 2) Click the 'Go Live' button or toggle switch, 3) Choose your session duration, 4) Your public page URL will be active and show you're live. Share your QR code or public page link with your audience!",
        "end live": "To end a live session: 1) Go to your Live Dashboard, 2) Click 'End Session' or toggle the Live switch off, 3) Confirm the end session, 4) Your session summary will show total requests, earnings, and duration. You can view session history anytime!",
        "live session": "A live session is when you're actively performing and accepting song requests. During a live session: Your public page shows you're LIVE, Fans can request songs from your catalogue, Requests appear in real-time, You can accept, skip, or remove requests, All requests are tracked for earnings. Make sure you have songs added before going live!",
        "what need before live": "Before going live, you need: 1) At least 5 songs in your catalogue (required), 2) Your profile URL set up, 3) Songs priced (minimum £2.00, recommended £3-4), 4) Your public page ready to share. You can add songs from the Catalogue/Setlist page. Use the Add Song Wizard for quick bulk uploads!",
        "schedule live": "Currently, live sessions start immediately when you click 'Go Live'. You can use the Calendar feature to schedule gigs and events, then go live when the time comes. Future updates may include scheduled live sessions - stay tuned!",
        "share live": "To share your live session: 1) When live, go to your Live Dashboard, 2) Click 'Share Your Profile' button, 3) Share your QR code or public page URL, 4) Your audience can scan the QR code or visit your public page to request songs. The QR code and link are always available in the Share section!",
        
        // Song Catalogue & Setlists
        "add songs": "To add songs: 1) Go to Catalogue/Setlist page, 2) Click 'Add Song' button, 3) Search for your song by title and artist, 4) Select from search results or add manually, 5) Set your price at least at your regional minimum shown in the app, 6) Add to catalogue. You can add songs one at a time from the same screen.",
        "search songs": "To search for songs: 1) Go to Catalogue page, 2) Use the search bar at the top, 3) Type song title or artist name, 4) Results appear instantly. You can also browse your entire catalogue and filter by tags. The search works across your entire song library!",
        "set song price": "To set song prices: 1) When adding or editing a song, enter the price in the Price field, 2) The app enforces your region's minimum price, 3) Pricing at or slightly above the minimum is fine to start; you can edit anytime from your catalogue.",
        "minimum price": "The minimum price for song requests depends on your currency and region. The Catalogue/Setlist screen shows the minimum when you add or edit a song. Use at least that amount so requests can be paid successfully.",
        "create setlist": "To create a setlist: 1) Go to Setlist page, 2) Click 'Create Setlist' or select existing songs, 3) Drag and drop songs to reorder, 4) Name your setlist, 5) Save for easy access. Setlists help you organize songs for specific gigs or performances. You can create multiple setlists!",
        "edit delete songs": "To edit songs: 1) Go to Catalogue page, 2) Click on a song, 3) Click 'Edit' button, 4) Update title, artist, or price, 5) Save changes. To delete: Click the song, then 'Delete' button. To archive: Click 'Archive' - archived songs are hidden but can be restored. You can also bulk edit multiple songs!",
        "bulk upload": "To add many songs quickly: use the Catalogue/Setlist page and add each song with title, artist, and price. You can repeat Add Song as many times as you need. If your app offers an onboarding or multi-add flow, you may see it once when your catalogue is empty.",
        "organize songs": "To organize songs: 1) Use tags to categorize songs (genre, mood, etc.), 2) Create setlists for specific performances, 3) Use the search to find songs quickly, 4) Archive old songs you don't use, 5) Drag to reorder in setlists. Tags help fans discover songs by category!",
        "song tags": "Song tags help categorize and organize your songs. You can add tags like genre (rock, pop, country), mood (upbeat, slow, romantic), or any custom tags. Tags help you and your audience find songs easily. Add tags when creating or editing songs!",
        "archive songs": "To archive songs: 1) Go to Catalogue page, 2) Click on a song, 3) Click 'Archive' button, 4) Archived songs are hidden from your main catalogue but can be restored. This is useful for songs you don't use often but want to keep. You can restore archived songs anytime!",
        
        // Payouts & Balances
        "check earnings": "To check your earnings: 1) Go to Payouts page, 2) View your total earnings, available balance, and pending payouts, 3) See breakdown by song requests and tips, 4) Check transaction history. Earnings update in real-time as requests come in. You can also see earnings in the Request Rewards dashboard!",
        "request payout": "To request a payout: 1) Go to Payouts page, 2) Click 'Request Payout' button, 3) Enter amount (minimum varies by payment method), 4) Select payment method (bank transfer, PayPal, etc.), 5) Enter payment details, 6) Submit request. Admins process payouts within 48 hours. You'll get a notification when processed!",
        "minimum payout": "Minimum payout amounts vary by payment method: Bank Transfer typically requires £10 minimum, PayPal may have different minimums, Stripe payouts have their own thresholds. Check the Payouts page for current minimum amounts. Instant payouts may have higher minimums than standard payouts.",
        "payout time": "Payout processing times: Standard payouts are processed within 48 hours by admins, Instant payouts (if available) process immediately but may have higher fees, Bank transfers typically take 3-5 business days after processing, PayPal transfers are usually instant after processing. Check your payout status in the Payouts page!",
        "payout history": "To view payout history: 1) Go to Payouts page, 2) Scroll to 'Payout History' section, 3) See all past payout requests with dates, amounts, methods, and statuses, 4) Filter by status (pending, approved, paid, rejected). Each payout shows full details including transaction ID when available!",
        "payment methods": "Available payment methods include: Bank Transfer (direct to your bank account), PayPal (instant transfers), Stripe (connected account payouts). Set up your preferred payment method in the Payouts page. You can change payment methods anytime. Some methods may require verification first!",
        "earnings calculated": "Earnings are calculated from: Song request payments (fans pay the price you set), Tips (100% goes to you), Platform fees are deducted (typically 10-15%), Your net earnings = Total payments - Platform fees. All earnings are tracked in real-time. View detailed breakdowns in your Payouts dashboard!",
        "when get paid": "You get paid when: 1) You request a payout (minimum amount required), 2) Admin processes your request (within 48 hours), 3) Payment is sent to your chosen method. Earnings accumulate in your balance until you request a payout. You can request payouts anytime once you reach the minimum amount!",
        "update payment details": "To update payment details: 1) Go to Payouts page, 2) Click 'Payment Settings' or 'Update Payment Method', 3) Enter new bank/PayPal details, 4) Save changes. Your new payment method will be used for future payouts. You may need to verify new payment methods before use!",
        
        // Request Rewards & Referrals
        "request rewards": "Request Rewards is our referral program! Here's how it works: 1) Share your referral link with other artists, 2) When they sign up using your link, you're connected, 3) You earn 10% commission on platform fees from their song requests, 4) Earnings update in real-time, 5) Request payouts when ready. Check your Rewards dashboard for your link and earnings!",
        "referral link": "Your referral link is in the Request Rewards dashboard. To share: 1) Go to Request Rewards page, 2) Copy your referral link, 3) Share via social media, email, or direct message, 4) When artists sign up using your link, you earn 10% of platform fees. The link format is: yoursite.com/signup?ref=YOURCODE. Share it anywhere!",
        "referral earnings": "Referral earnings work like this: When an artist you referred receives a £3 song request, you earn 10% of the platform fee (typically £0.30). The referred artist keeps 100% of their earnings - your commission comes from platform fees, not their income. Earnings update in real-time. View all referral earnings in your Rewards dashboard!",
        
        // Song Requests Management
        "manage requests": "To manage song requests: 1) During live sessions, requests appear in real-time in your Live Dashboard, 2) Click 'Accept' to add to queue, 3) Use 'Skip' to move to next, 4) Click 'Remove' to decline, 5) View request details (song, requester name, amount paid), 6) See request history after session ends. Requests are ordered by payment amount and time!",
        "song requests": "Song requests work like this: 1) Fans visit your public page when you're live, 2) They select a song from your catalogue, 3) They pay the price you set (plus optional tip), 4) Request appears in your Live Dashboard instantly, 5) You accept and play the song, 6) Earnings are tracked automatically. Paid requests can skip the queue!",
        "accept requests": "To accept requests: 1) View incoming requests in Live Dashboard, 2) Click 'Accept' button on a request, 3) Request moves to your queue, 4) Play the song during your performance, 5) Mark as 'Played' when done. You can also auto-accept requests in settings. Accepted requests are tracked for earnings!",
        "skip requests": "To skip requests: 1) Click 'Skip' button on a request, 2) Request is moved to skipped list, 3) Next request in queue becomes active. Skipped requests don't affect earnings - the fan has already paid. You can view skipped requests later. Use skip when a song doesn't fit your current set!",
        
        // Profile & Settings
        "update profile": "To update your profile: 1) Go to Profile page, 2) Edit artist name, tagline, bio, 3) Upload profile picture and header image, 4) Add social media links, 5) Update contact information, 6) Save changes. Your profile appears on your public page. Keep it updated to attract more fans!",
        "change profile url": "Your profile URL (url_slug) is set when you first create your account and CANNOT be changed. This ensures consistency and prevents broken links. Your public page URL is permanent: yoursongrequest.com/YOUR-SLUG. Choose your URL carefully during signup - it's your permanent identity on the platform!",
        "profile picture": "To add/update profile picture: 1) Go to Profile page, 2) Click on your current picture or 'Upload' button, 3) Select image from your device, 4) Crop/resize if needed, 5) Save. Recommended size: 500x500px square. Your profile picture appears on your public page and in the app!",
        "header image": "To add a header image: 1) Go to Profile page, 2) Scroll to 'Header Image' section, 3) Click 'Upload Header Image', 4) Select image (recommended: 1200x400px), 5) Save. Header images appear at the top of your public page. Use high-quality images that represent your brand!",
        
        // Calendar & Gigs
        "schedule gigs": "To schedule gigs: 1) Go to Calendar page, 2) Click 'Add Event' or 'New Gig', 3) Enter event details (name, date, time, location), 4) Add description and venue info, 5) Set if it's public or private, 6) Save. Gigs appear on your calendar and can be shown on your public page. Fans can see upcoming gigs!",
        "calendar": "The Calendar feature helps you: 1) Schedule gigs and events, 2) View all upcoming performances, 3) Share gig dates with fans, 4) Organize your performance schedule, 5) Set reminders. Gigs can be displayed on your public page so fans know when you're performing. Add all your shows!",
        
        // Creative Assets
        "creative assets": "Creative Assets are promotional tools including: 1) QR codes for your public page, 2) Social media graphics, 3) Promotional images, 4) Downloadable assets for marketing. Access Creative Assets from the Promo Tools page. Use these to promote your profile and attract more fans!",
        
        // General Help
        "melody": "I'm Melody, your AI assistant! I can help with: Going live and live sessions, Adding and managing songs, Creating setlists, Managing song requests, Request Rewards and referrals, Earnings and payouts, Profile management, Calendar and gigs, Creative assets, and much more. Just ask me anything about Your Song Request!",
        "help": "I can help with: Live streaming and sessions, Song catalogue management, Setlists creation, Request handling, Referral program (Request Rewards), Earnings tracking, Payout requests, Profile updates, Calendar and gig scheduling, Creative assets, Payment setup, and general platform questions. What would you like to know?",
        "support": "For additional support beyond what I can help with, I can connect you with our support team. Would you like to send a message to our support team? They can help with technical issues, account problems, or complex questions.",
        "contact": "I can help you contact our support team. Would you like to send a message about a specific issue or question? Fill out the contact form and our team will get back to you as soon as possible.",
        
        // Platform Information
        "platform": "Your Song Request is a platform that connects artists with their audience. Artists create a catalogue of songs, set prices, go live, and fans request songs by paying. Artists earn money from requests and tips. The platform handles payments securely through Stripe. It's perfect for live performances, gigs, and connecting with fans!",
        "how it works": "Here's how Your Song Request works: 1) Artists sign up and create profiles, 2) Add songs to catalogue with prices, 3) Go live to start accepting requests, 4) Fans visit public page and request songs, 5) Artists accept requests and play songs, 6) Artists earn money from requests and tips, 7) Request payouts when ready. It's that simple!",
        "public page": "Your public page is where fans interact with you. It shows: Your profile info and photos, Your song catalogue, Live status when you're performing, Request queue, Ability to request songs and send tips. Your public page URL is: yoursongrequest.com/YOUR-SLUG. Share this with your audience!",
        "qr code": "Your QR code links directly to your public page. To get it: 1) Go to Live Dashboard, 2) Click 'Share Your Profile', 3) View/download your QR code, 4) Share QR code at gigs, on social media, or print it. Fans scan the QR code to instantly access your public page. It's perfect for live performances!",
        
        // Tips
        "tips": "Fans can send you tips! Tips work like this: 1) Fans visit your public page, 2) Click 'Send a Tip', 3) Enter amount and optional message, 4) Complete payment via Stripe, 5) Tip appears in your dashboard instantly, 6) 100% of tips go to you. Tips are separate from song requests and are a great way for fans to show appreciation!",
        "send tip": "Fans can send tips from your public page: 1) Click 'Send a Tip' button, 2) Enter their name and tip amount, 3) Add optional message, 4) Complete payment, 5) You receive notification. Tips are processed through Stripe and appear in your earnings immediately. Tips are a great way for fans to support you!",
        
        // Payment & Stripe
        "stripe": "Stripe is our secure payment processor. When you sign up, a Stripe Connect account is created for you. This allows: Secure payment processing, Direct payouts to your bank account, Payment tracking and history, Compliance with payment regulations. Your Stripe account is automatically set up - no extra steps needed!",
        "payment": "Payments work securely through Stripe: 1) Fans pay for song requests via Stripe, 2) Payments are processed instantly, 3) Funds go to your Stripe Connect account, 4) You can request payouts to your bank, 5) All transactions are tracked. Stripe handles all security and compliance. Your financial data is safe!",
        
    };

    useEffect(() => {
        if (isOpen) {
            loadSupportConversations();
            checkReferralSystemEnabled();
            setMelodyMainTab('faqs');
            setFaqAnswerSheet(null);
            setExpandedCategories({});
        }
    }, [isOpen]);

    const checkReferralSystemEnabled = async () => {
        try {
            const enabled = await isReferralSystemEnabled();
            setReferralSystemEnabled(enabled);
        } catch (error) {
            console.error('Error checking referral system status:', error);
            setReferralSystemEnabled(true); // Default to enabled on error
        }
    };

    // Apply open intent from parent (fires after Melody is mounted — fixes race with window events)
    useEffect(() => {
        if (!isOpen || intent === null) return;
        let cancelled = false;
        (async () => {
            try {
                if (intent.reason === 'support_response') {
                    await loadSupportConversations();
                    if (!cancelled) setShowSupportConversation(true);
                }
                if (intent.openContactForm) {
                    if (!cancelled) setShowContactForm(true);
                }
            } finally {
                if (!cancelled) onIntentHandled?.();
            }
        })();
        return () => { cancelled = true; };
    }, [isOpen, intent, onIntentHandled]);

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    const loadSupportConversations = async () => {
        if (!user) {
            console.log('❌ No user found for loading support conversations');
            return;
        }
        
        console.log('🔍 Loading support conversations for user:', user.uid);
        
        try {
            // Load all support responses for this user
            const q = query(
                collection(db, 'support_responses'),
                where('userId', '==', user.uid),
                orderBy('createdAt', 'asc')
            );
            const querySnapshot = await getDocs(q);
            
            console.log('📋 Found', querySnapshot.docs.length, 'support responses');
            
            if (!querySnapshot.empty) {
                const allResponses = querySnapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                }));
                
                console.log('📝 Support responses:', allResponses);
                setSupportConversationHistory(allResponses);
                
                // Set the latest response as the current conversation
                const latestResponse = allResponses[allResponses.length - 1];
                setSupportConversation(latestResponse);
                
                // Load the original submission if we have a submissionId
                if (latestResponse.submissionId) {
                    console.log('🔍 Loading original submission:', latestResponse.submissionId);
                    try {
                        const submissionDoc = await getDoc(doc(db, 'contact_submissions', latestResponse.submissionId));
                        if (submissionDoc.exists()) {
                            const submissionData = {
                                id: submissionDoc.id,
                                ...submissionDoc.data()
                            };
                            console.log('📄 Original submission loaded:', submissionData);
                            setOriginalSubmission(submissionData);
                        } else {
                            console.log('❌ Original submission not found');
                        }
                    } catch (error) {
                        console.error('Error loading original submission:', error);
                    }
                } else {
                    console.log('❌ No submissionId found in latest response');
                }
            } else {
                console.log('❌ No support responses found');
                setSupportConversationHistory([]);
                setSupportConversation(null);
                setOriginalSubmission(null);
            }
        } catch (error) {
            console.error('Error loading support conversations:', error);
        }
    };

    const isSupportResolved = () => {
        // Check if the latest support response indicates resolution
        return supportConversation?.data?.resolved === true;
    };

    const toggleCategory = (categoryId) => {
        setExpandedCategories(prev => ({
            ...prev,
            [categoryId]: !prev[categoryId]
        }));
    };

    const getQuickAnswer = (rawMessage) => {
        const lowerMessage = String(rawMessage).toLowerCase();
        for (const [key, response] of Object.entries(chatbotResponses)) {
            if (lowerMessage.includes(key)) {
                if (key === 'request rewards' || key === 'referral link' || key === 'referral earnings') {
                    if (!referralSystemEnabled) {
                        if (key === 'request rewards') {
                            return "Request Rewards is our referral program! Here's how it works: 1) Share your referral link with other artists, 2) When they sign up using your link, you're connected, 3) Help grow the platform by referring artists. Check your Rewards dashboard for your link!";
                        }
                        if (key === 'referral link') {
                            return "Your referral link is in the Request Rewards dashboard. To share: 1) Go to Request Rewards page, 2) Copy your referral link, 3) Share via social media, email, or direct message, 4) When artists sign up using your link, you help grow the platform. The link format is: yoursite.com/signup?ref=YOURCODE. Share it anywhere!";
                        }
                        if (key === 'referral earnings') {
                            return "Referrals help grow the platform! When you refer artists, they join the community and can start earning. While referral commissions are currently disabled, referring artists is still valuable for building the platform community.";
                        }
                    }
                }
                return response;
            }
        }
        return null;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!inputValue.trim() || isTyping) return;

        setMelodyMainTab('chat');
        const userMessage = inputValue.trim();
        setInputValue('');
        
        // Add user message immediately
        const userMsg = {
            id: Date.now(),
            type: 'user',
            content: userMessage
        };
        setMessages(prev => [...prev, userMsg]);
        
        // Collapse all categories after submitting
        setExpandedCategories({});
        
        setIsTyping(true);

        // Simulate typing delay
        setTimeout(async () => {
            const response = await generateResponse(userMessage);
            const botMsg = {
                id: Date.now() + 1,
                type: 'bot',
                content: response
            };
            setMessages(prev => [...prev, botMsg]);
            setIsTyping(false);
        }, 1000);
    };

    const generateResponse = async (message) => {
        const matched = getQuickAnswer(message);
        if (matched) return matched;

        setShowContactForm(true);
        return "I'm not sure about that specific question. Let me connect you with our support team who can provide more detailed assistance.";
    };

    const handleContactFormSubmit = async (e) => {
        e.preventDefault();
        if (!user) return;

        setIsSubmittingContact(true);
        try {
            const submissionDoc = await addDoc(collection(db, 'contact_submissions'), {
                userId: user.uid,
                userEmail: user.email,
                subject: contactForm.subject,
                message: contactForm.message,
                status: 'new',
                createdAt: serverTimestamp()
            });

            // Add user message to conversation
            const userMessage = {
                id: Date.now(),
                type: 'user',
                content: `Subject: ${contactForm.subject}\n\n${contactForm.message}`
            };
            setMessages(prev => [...prev, userMessage]);

            // Add Melody's response to the conversation immediately
            const melodyResponse = {
                id: Date.now() + 1,
                type: 'bot',
                content: "Thank you for reaching out! I've sent your support request to our team. We'll review your message and get back to you as soon as possible. You'll receive a notification when we respond."
            };
            
            setMessages(prev => [...prev, melodyResponse]);

            // Collapse all categories and close contact form
            setExpandedCategories({});
            setContactForm({ subject: '', message: '' });
            setShowContactForm(false);
            setMelodyMainTab('chat');
            
            // Scroll to bottom to show the conversation
            setTimeout(() => {
                scrollToBottom();
            }, 100);
            
            toast({
                title: "Message Sent",
                description: "Your support request has been sent successfully.",
                variant: "default"
            });
        } catch (error) {
            console.error('Error submitting contact form:', error);
            toast({
                title: "Error",
                description: "Failed to send message. Please try again.",
                variant: "destructive",
            });
        } finally {
            setIsSubmittingContact(false);
        }
    };

    const submitSupportResponse = async () => {
        if (!user || !supportResponse.trim()) return;

        setIsSubmittingSupportResponse(true);
        try {
            // Create support response
            await addDoc(collection(db, 'support_responses'), {
                userId: user.uid,
                message: supportResponse,
                createdAt: serverTimestamp()
            });

            // Send notification to admin
            await addDoc(collection(db, 'admin_notifications'), {
                type: 'support_response',
                title: 'New Support Response',
                message: `Artist ${user.email} replied to support conversation: "${supportResponse.substring(0, 100)}..."`,
                userId: user.uid,
                targetUserId: user.uid,
                createdAt: serverTimestamp(),
                read: false
            });

            toast({
                title: "Reply Sent",
                description: "Your reply has been sent to the support team.",
            });

            setSupportResponse('');
            setShowSupportConversation(false);
        } catch (error) {
            console.error('Error submitting support response:', error);
            toast({
                title: "Error",
                description: "Failed to send reply. Please try again.",
                variant: "destructive",
            });
        } finally {
            setIsSubmittingSupportResponse(false);
        }
    };

    // Mobile-first design - no dialog wrapper, just a full-screen mobile interface
    if (!isOpen) return null;

    return createPortal((
        <div
            role="dialog"
            aria-modal="true"
            aria-label="Melody help"
            className="fixed inset-0 flex flex-col min-h-0 h-[100dvh] max-h-[100dvh] w-full overflow-hidden bg-[#050816] text-white isolate touch-manipulation bg-[radial-gradient(circle_at_top_left,rgba(59,130,246,0.22),transparent_36%),radial-gradient(circle_at_bottom_right,rgba(168,85,247,0.18),transparent_38%),linear-gradient(180deg,#020617_0%,#050816_44%,#020617_100%)]"
            style={{ zIndex: MELODY_Z_MAIN }}
        >
            <div className={ysr2Mobile.orbBlue} />
            <div className={ysr2Mobile.orbPurple} />
            <div className="h-[max(0.5rem,env(safe-area-inset-top))] bg-slate-950/90 flex-shrink-0" />

            <div className="relative z-10 bg-slate-950/80 backdrop-blur-2xl border-b border-blue-300/20 px-4 py-3 flex-shrink-0">
                <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center space-x-3 min-w-0">
                        <div className="w-9 h-9 bg-gradient-to-br from-purple-600 to-blue-600 rounded-2xl flex items-center justify-center shrink-0 shadow-[0_12px_40px_rgba(88,28,135,0.45)] border border-white/10">
                            <Bot className="w-5 h-5 text-white" />
                        </div>
                        <div className="min-w-0">
                            <h1 className="text-lg font-semibold text-white truncate tracking-tight">Melody</h1>
                            <p className="text-[11px] text-blue-100/65 font-medium uppercase tracking-[0.18em]">Artist support</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-0.5 shrink-0">
                        <button
                            type="button"
                            onClick={() => setShowContactForm(true)}
                            className={`${ysr2Mobile.primaryButton} px-3 py-2 text-xs font-semibold touch-manipulation`}
                        >
                            Message
                        </button>
                        {supportConversation && (
                            <button
                                type="button"
                                onClick={() => setShowSupportConversation(true)}
                                className="p-2.5 text-blue-300 hover:text-white touch-manipulation rounded-xl hover:bg-white/10"
                                aria-label="Open support thread"
                            >
                                <MessageCircle className="w-5 h-5" />
                            </button>
                        )}
                        <button
                            type="button"
                            onClick={onClose}
                            className="p-2.5 text-slate-400 hover:text-white touch-manipulation rounded-xl hover:bg-white/10"
                            aria-label="Close"
                        >
                            <X className="w-5 h-5" />
                        </button>
                    </div>
                </div>
                <div className="mt-3 flex rounded-2xl p-1 bg-black/45 border border-white/10 shadow-[inset_0_1px_0_rgba(255,255,255,0.06)]">
                    <button
                        type="button"
                        className={`flex-1 py-2.5 text-xs font-semibold rounded-[0.9rem] transition-all touch-manipulation ${
                            melodyMainTab === 'faqs'
                                ? 'bg-gradient-to-r from-blue-600 to-blue-500 text-white shadow-[0_10px_28px_rgba(37,99,235,0.35)]'
                                : 'text-slate-400 hover:text-white'
                        }`}
                        onClick={() => setMelodyMainTab('faqs')}
                    >
                        Guides
                    </button>
                    <button
                        type="button"
                        className={`flex-1 py-2.5 text-xs font-semibold rounded-[0.9rem] transition-all touch-manipulation ${
                            melodyMainTab === 'chat'
                                ? 'bg-gradient-to-r from-blue-600 to-blue-500 text-white shadow-[0_10px_28px_rgba(37,99,235,0.35)]'
                                : 'text-slate-400 hover:text-white'
                        }`}
                        onClick={() => setMelodyMainTab('chat')}
                    >
                        Chat
                    </button>
                </div>
            </div>

            <div className="relative z-10 flex-1 flex flex-col min-h-0 overflow-hidden">
                {melodyMainTab === 'faqs' ? (
                    <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-4 py-4">
                        <p className={`${ysr2Mobile.pill} mb-4`}>Topic guides</p>
                        <div className="space-y-3">
                            {questionCategories.map((category) => (
                                <div key={category.id} className={`${ysr2Mobile.surface} border-blue-400/15 overflow-hidden p-0.5`}>
                                    <button
                                        type="button"
                                        onClick={() => toggleCategory(category.id)}
                                        className="w-full text-left p-3.5 text-gray-100 hover:bg-white/[0.06] transition-colors flex items-center justify-between text-sm font-semibold rounded-[1.25rem] touch-manipulation"
                                    >
                                        <span>{category.title}</span>
                                        {expandedCategories[category.id] ? (
                                            <ChevronUp className="w-4 h-4 text-blue-300/80 shrink-0" />
                                        ) : (
                                            <ChevronDown className="w-4 h-4 text-blue-300/80 shrink-0" />
                                        )}
                                    </button>
                                    {expandedCategories[category.id] && (
                                        <div className="bg-black/25 border border-white/10 rounded-2xl p-2 space-y-1.5 mt-1 mx-0.5 mb-1">
                                            {category.questions.map((question, index) => (
                                                <button
                                                    key={index}
                                                    type="button"
                                                    onClick={() => {
                                                        if (question === 'How can I contact you?') {
                                                            setExpandedCategories({});
                                                            setShowContactForm(true);
                                                            return;
                                                        }
                                                        const ans = getQuickAnswer(question);
                                                        setFaqAnswerSheet({
                                                            question,
                                                            answer:
                                                                ans ||
                                                                "I don't have a stored answer for this one. Try the Chat tab and ask in your own words, or tap Message to reach our team.",
                                                        });
                                                    }}
                                                    className="w-full text-left p-3 bg-white/[0.04] hover:bg-blue-500/15 border border-white/10 rounded-xl text-blue-100/95 hover:text-white text-xs sm:text-sm leading-snug transition-colors touch-manipulation active:scale-[0.99]"
                                                >
                                                    {question}
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                ) : (
                    <>
                        <div className="flex-1 overflow-y-auto overscroll-contain px-4 py-3 space-y-3 min-h-0">
                            {messages.length === 0 && !isTyping && (
                                <div className={`${ysr2Mobile.surface} border-blue-400/15 p-5 text-center`}>
                                    <p className="text-sm text-blue-100/85 leading-relaxed">
                                        Ask anything about live sessions, payouts, catalogue, or your profile — Melody will do its best to help.
                                    </p>
                                </div>
                            )}
                            {messages.map((message) => (
                                <div
                                    key={message.id}
                                    className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
                                >
                                    <div
                                        className={`max-w-[88%] p-3.5 rounded-2xl text-sm leading-relaxed ${
                                            message.type === 'user'
                                                ? 'bg-gradient-to-br from-blue-600 to-blue-700 text-white shadow-[0_12px_32px_rgba(37,99,235,0.28)] border border-white/10'
                                                : 'bg-white/[0.07] text-gray-100 border border-white/10 shadow-[0_8px_30px_rgba(0,0,0,0.25)]'
                                        }`}
                                    >
                                        <p className="whitespace-pre-wrap">{message.content}</p>
                                    </div>
                                </div>
                            ))}
                            {isTyping && (
                                <div className="flex justify-start">
                                    <div className="bg-white/[0.07] text-gray-200 border border-white/10 p-3.5 rounded-2xl">
                                        <div className="flex items-center space-x-2">
                                            <Bot className="w-4 h-4 text-purple-400" />
                                            <div className="flex space-x-1">
                                                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" />
                                                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }} />
                                                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }} />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                            <div ref={messagesEndRef} />
                        </div>
                        <div className="bg-slate-950/90 backdrop-blur-2xl border-t border-blue-300/20 px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] flex-shrink-0">
                            <form onSubmit={handleSubmit} className="flex gap-2 items-center">
                                <input
                                    type="text"
                                    value={inputValue}
                                    onChange={(e) => setInputValue(e.target.value)}
                                    placeholder="Ask me anything…"
                                    className={`flex-1 min-w-0 ${ysr2Mobile.input} text-base py-3`}
                                    disabled={isTyping}
                                    autoComplete="off"
                                    autoCorrect="off"
                                    autoCapitalize="off"
                                    spellCheck="false"
                                />
                                <button
                                    type="submit"
                                    disabled={!inputValue.trim() || isTyping}
                                    className={`${ysr2Mobile.primaryButton} p-3 disabled:opacity-45 touch-manipulation shrink-0`}
                                    aria-label="Send"
                                >
                                    <Send className="w-5 h-5" />
                                </button>
                            </form>
                        </div>
                    </>
                )}
            </div>

            {faqAnswerSheet && (
                <div
                    className="fixed inset-0 flex flex-col h-[100dvh] max-h-[100dvh] bg-[#050816] text-white"
                    style={{ zIndex: MELODY_Z_OVERLAY }}
                >
                    <div className="h-[max(0.5rem,env(safe-area-inset-top))] bg-slate-950/90 flex-shrink-0" />
                    <div className="relative z-10 flex items-center gap-2 px-3 py-3 border-b border-white/10 bg-slate-950/80 backdrop-blur-xl flex-shrink-0">
                        <button
                            type="button"
                            onClick={() => setFaqAnswerSheet(null)}
                            className="flex items-center gap-1 rounded-xl px-3 py-2 text-sm font-semibold text-blue-200 hover:bg-white/10 touch-manipulation"
                        >
                            <ChevronLeft className="w-5 h-5" />
                            Guides
                        </button>
                    </div>
                    <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-4 py-4">
                        <h2 className="text-base font-semibold text-white leading-snug mb-4">{faqAnswerSheet.question}</h2>
                        <div className={`${ysr2Mobile.surfaceStrong} border-blue-400/20 p-5`}>
                            <p className="text-sm text-slate-100/95 whitespace-pre-wrap leading-relaxed">{faqAnswerSheet.answer}</p>
                        </div>
                        <button
                            type="button"
                            onClick={() => setFaqAnswerSheet(null)}
                            className={`mt-6 w-full ${ysr2Mobile.secondaryButton} py-3.5 text-sm font-semibold touch-manipulation`}
                        >
                            Back to topics
                        </button>
                    </div>
                </div>
            )}

            {/* Contact Form - Full Screen Overlay */}
            {showContactForm && (
                <div
                    className="fixed inset-0 flex flex-col h-[100dvh] max-h-[100dvh] overflow-hidden bg-[#050816] text-white touch-manipulation bg-[radial-gradient(circle_at_top_left,rgba(59,130,246,0.18),transparent_40%),radial-gradient(circle_at_bottom_right,rgba(168,85,247,0.14),transparent_42%),linear-gradient(180deg,#020617_0%,#050816_50%,#020617_100%)]"
                    style={{ zIndex: MELODY_Z_OVERLAY }}
                >
                    <div className="pointer-events-none absolute -top-24 -left-16 h-64 w-64 rounded-full bg-blue-600/18 blur-3xl" />
                    <div className="pointer-events-none absolute -bottom-20 -right-16 h-72 w-72 rounded-full bg-purple-600/15 blur-3xl" />
                    <div className="h-[max(0.5rem,env(safe-area-inset-top))] bg-slate-950/90 flex-shrink-0" />
                    <div className="relative z-10 border-b border-white/10 bg-slate-950/85 backdrop-blur-2xl px-4 py-4 flex-shrink-0">
                        <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0 flex items-start gap-3">
                                <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-purple-600/90 to-blue-600/90 flex items-center justify-center border border-white/10 shadow-[0_14px_40px_rgba(88,28,135,0.35)] shrink-0">
                                    <Mail className="w-5 h-5 text-white" />
                                </div>
                                <div className="min-w-0">
                                    <p className={`${ysr2Mobile.pill} mb-2`}>Write to us</p>
                                    <h2 className="text-lg font-semibold text-white leading-tight">Message the team</h2>
                                    <p className="text-xs text-blue-100/65 mt-1 leading-relaxed">
                                        We read every message and reply as soon as we can. You will get a notification when we respond.
                                    </p>
                                </div>
                            </div>
                            <button
                                type="button"
                                onClick={() => setShowContactForm(false)}
                                className="p-2.5 rounded-xl text-slate-400 hover:text-white hover:bg-white/10 touch-manipulation shrink-0"
                                aria-label="Close"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                    </div>

                    <div className="relative z-10 flex-1 min-h-0 overflow-y-auto overscroll-contain px-4 py-5">
                        <form id="melody-contact-form" onSubmit={handleContactFormSubmit} className={`${ysr2Mobile.glowCard} border-purple-400/20 p-5 space-y-5`}>
                            <div>
                                <label className="block text-[11px] font-semibold uppercase tracking-[0.2em] text-blue-100/70 mb-2">Subject</label>
                                <input
                                    type="text"
                                    value={contactForm.subject}
                                    onChange={(e) => setContactForm(prev => ({ ...prev, subject: e.target.value }))}
                                    placeholder="What do you need help with?"
                                    className={`w-full ${ysr2Mobile.input} text-base min-h-[48px]`}
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-[11px] font-semibold uppercase tracking-[0.2em] text-blue-100/70 mb-2">Message</label>
                                <textarea
                                    value={contactForm.message}
                                    onChange={(e) => setContactForm(prev => ({ ...prev, message: e.target.value }))}
                                    placeholder="Share details so we can help faster…"
                                    className={`w-full ${ysr2Mobile.input} resize-none min-h-[168px] text-base leading-relaxed`}
                                    rows={7}
                                    required
                                />
                            </div>
                        </form>
                    </div>

                    <div className="relative z-10 border-t border-white/10 bg-slate-950/95 backdrop-blur-xl px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] flex-shrink-0">
                        <div className="flex gap-3">
                            <button
                                type="button"
                                onClick={() => setShowContactForm(false)}
                                className={`flex-1 ${ysr2Mobile.secondaryButton} py-3.5 text-sm font-semibold touch-manipulation`}
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                form="melody-contact-form"
                                disabled={isSubmittingContact}
                                className={`flex-1 ${ysr2Mobile.primaryButton} py-3.5 text-sm font-semibold disabled:opacity-50 touch-manipulation`}
                            >
                                {isSubmittingContact ? 'Sending…' : 'Send message'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Support Conversation - Full Screen Overlay */}
            {showSupportConversation && supportConversation && (
                <div
                    className="fixed inset-0 flex flex-col h-[100dvh] max-h-[100dvh] overflow-hidden bg-[#050816] text-white"
                    style={{ zIndex: MELODY_Z_OVERLAY }}
                >
                    <div className="h-[max(0.5rem,env(safe-area-inset-top))] bg-slate-950/90 flex-shrink-0" />
                    <div className="bg-slate-950/80 border-b border-blue-300/20 px-4 py-3 flex-shrink-0">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-2">
                                {isSupportResolved() ? (
                                    <Check className="w-5 h-5 text-green-400" />
                                ) : (
                                    <MessageCircle className="w-5 h-5 text-blue-400" />
                                )}
                                <h2 className="text-lg font-semibold text-white">
                                    {isSupportResolved() ? 'Support Resolved' : 'Support Response'}
                                </h2>
                            </div>
                            <button
                                onClick={() => setShowSupportConversation(false)}
                                className="p-2 text-gray-400 hover:text-white"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                    </div>
                    
                    <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-4 py-4">
                        {/* Conversation History */}
                        {supportConversationHistory.length > 0 ? (
                            <div className="space-y-4">
                                {/* Original Submission */}
                                {originalSubmission && (
                                    <div className="p-4 rounded-lg border bg-blue-900/20 border-blue-700 mr-8">
                                        <div className="flex items-start justify-between mb-2">
                                            <div className="flex items-center space-x-2">
                                                <Mail className="w-4 h-4 text-gray-400" />
                                                <span className="text-sm font-medium text-gray-400">You</span>
                                                <span className="text-xs text-gray-500">(Original Request)</span>
                                            </div>
                                            <span className="text-xs text-gray-500">
                                                {originalSubmission.createdAt?.toDate ? 
                                                    originalSubmission.createdAt.toDate().toLocaleString() : 
                                                    'Recently'
                                                }
                                            </span>
                                        </div>
                                        <div className="mb-2">
                                            <span className="text-xs text-gray-400">Subject: </span>
                                            <span className="text-sm text-gray-300 font-medium">{originalSubmission.subject}</span>
                                        </div>
                                        <p className="text-gray-200 text-sm whitespace-pre-wrap">
                                            {originalSubmission.message}
                                        </p>
                                    </div>
                                )}
                                
                                {/* Support Responses */}
                                {supportConversationHistory.map((response, index) => (
                                    <div key={response.id} className={`p-4 rounded-lg border ${
                                        response.fromAdmin 
                                            ? 'bg-gray-800 border-gray-700 ml-8' 
                                            : 'bg-blue-900/20 border-blue-700 mr-8'
                                    }`}>
                                        <div className="flex items-start justify-between mb-2">
                                            <div className="flex items-center space-x-2">
                                                {response.fromAdmin ? (
                                                    <>
                                                        <MessageCircle className="w-4 h-4 text-blue-400" />
                                                        <span className="text-sm font-medium text-blue-400">Support Team</span>
                                                    </>
                                                ) : (
                                                    <>
                                                        <Mail className="w-4 h-4 text-gray-400" />
                                                        <span className="text-sm font-medium text-gray-400">You</span>
                                                    </>
                                                )}
                                            </div>
                                            <span className="text-xs text-gray-500">
                                                {response.createdAt?.toDate ? 
                                                    response.createdAt.toDate().toLocaleString() : 
                                                    'Recently'
                                                }
                                            </span>
                                        </div>
                                        <p className="text-gray-200 text-sm whitespace-pre-wrap">
                                            {response.message}
                                        </p>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="bg-gray-800 p-4 rounded-lg border border-gray-700 mb-4">
                                <p className="text-gray-200 text-sm whitespace-pre-wrap">
                                    {supportConversation.message}
                                </p>
                                <p className="text-xs text-gray-400 mt-2">
                                    Support Team • {supportConversation.createdAt?.toDate ? 
                                        supportConversation.createdAt.toDate().toLocaleString() : 
                                        'Recently'
                                    }
                                </p>
                            </div>
                        )}
                        
                        {isSupportResolved() ? (
                            <div className="bg-green-900/20 border border-green-700 p-4 rounded-lg">
                                <div className="flex items-center space-x-2 mb-2">
                                    <Check className="w-5 h-5 text-green-400" />
                                    <h3 className="text-green-400 font-medium">Support Request Resolved</h3>
                                </div>
                                <p className="text-gray-300 text-sm mb-4">
                                    This support request has been marked as resolved. If you need further assistance, please start a new support request.
                                </p>
                                <button
                                    onClick={() => {
                                        setShowSupportConversation(false);
                                        setSupportResponse('');
                                    }}
                                    className="w-full bg-gray-700 hover:bg-gray-600 text-white py-3 rounded-lg text-sm"
                                >
                                    Close
                                </button>
                            </div>
                        ) : (
                            <form onSubmit={(e) => { e.preventDefault(); submitSupportResponse(); }} className="space-y-4">
                                <div>
                                    <label className="block text-sm text-gray-300 mb-1">Your Reply</label>
                                    <textarea
                                        value={supportResponse}
                                        onChange={(e) => setSupportResponse(e.target.value)}
                                        placeholder="Type your reply to the support team..."
                                        className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-3 text-white placeholder-gray-400 text-sm focus:outline-none focus:border-blue-500 resize-none"
                                        rows={4}
                                        required
                                    />
                                </div>
                                
                                <div className="flex gap-3">
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setShowSupportConversation(false);
                                            setSupportResponse('');
                                        }}
                                        className="flex-1 bg-gray-700 hover:bg-gray-600 text-white py-3 rounded-lg text-sm"
                                    >
                                        Close
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={isSubmittingSupportResponse || !supportResponse.trim()}
                                        className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 text-white py-3 rounded-lg text-sm"
                                    >
                                        {isSubmittingSupportResponse ? 'Sending...' : 'Send Reply'}
                                    </button>
                                </div>
                            </form>
                        )}
                    </div>
                    
                    <div className="bg-gray-900 border-t border-gray-700 px-4 py-3 pb-12 sm:pb-3">
                        {/* Form buttons moved inside form above */}
                    </div>
                </div>
            )}
        </div>
    ), document.body);
};

export default MelodyChatbot;